import { ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import { saveAs } from 'file-saver';
import { useCallback, useState } from 'react';

import { Button } from '@/components/ui/button';
import { useReportData } from '@/hooks/useReportData';
import { cn } from '@/lib/utils';
import { TimeHistoryReportFormValues } from '@/types/TimeHistoryReportFormValues';

import { EgSpinner } from '../EgSpinner';
import { TimeHistoryChart } from './TimeHistoryChart';
import { TimeHistoryReportForm } from './TimeHistoryReportForm';
import TimeHistoryReportTable from './TimeHistoryReportTable';

export const TimeHistoryReport = () => {
  const [waitingForFetch, setWaitingForFetch] = useState(false);
  const [formData, setFormData] = useState<TimeHistoryReportFormValues>({
    startDate: null,
    endDate: null,
    projects: [],
    projectTasks: [],
    projectCategories: [],
  });

  const { getFilteredTimesheetEntries, reportData } = useReportData();

  const downloadCsv = useCallback(() => {
    if (!reportData?.timeSheet) return;

    const timeSheet = getFilteredTimesheetEntries(formData);
    if (!timeSheet) return;

    const userContext = reportData?.userContext;

    const headers = ['Resource', 'Date', 'Project', 'Task', 'Category', 'Description, Hours'];
    const csvData = timeSheet.map((entry) => [
      userContext.fullName,
      new Date(entry.entryDate).toLocaleDateString(),
      entry.project?.displayName || '',
      entry.projectTask?.displayName || '',
      entry.projectCategory?.displayName || '',
      entry.description,
      entry.hours.toString(),
    ]);

    const csvContent = [
      headers.join(','),
      ...csvData.map((row) => row.map((cell) => `"${cell}"`).join(',')),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const filename = 'time-history-report.csv';

    saveAs(blob, filename);
  }, [formData, getFilteredTimesheetEntries, reportData?.timeSheet, reportData?.userContext]);

  const onSubmit = useCallback(
    (formData: TimeHistoryReportFormValues) => {
      const timeSheet = getFilteredTimesheetEntries(formData);
      const selectedTasks = formData.projectTasks;
      const selectedCategories = formData.projectCategories;

      // Remove unrelated tasks and categories if related project has been unselected
      selectedTasks?.forEach((task) => {
        if (!timeSheet?.some((entry) => entry.projectTask?.id === task.id)) {
          const index = selectedTasks.indexOf(task);
          if (index > -1) {
            selectedTasks.splice(index, 1);
          }
        }
      });

      selectedCategories?.forEach((category) => {
        if (!timeSheet?.some((entry) => entry.projectCategory?.id === category.id)) {
          const index = selectedCategories.indexOf(category);
          if (index > -1) {
            selectedCategories.splice(index, 1);
          }
        }
      });
      formData.projectTasks = selectedTasks;
      formData.projectCategories = selectedCategories;

      setFormData(formData);
    },
    [getFilteredTimesheetEntries]
  );

  return (
    <div className="flex h-full w-full grow">
      <div className="flex w-full gap-5 p-5">
        <aside className="flex w-min rounded-md border bg-list/95 p-5">
          <TimeHistoryReportForm onSubmit={onSubmit} setWaitingForFetch={setWaitingForFetch} />
        </aside>
        <main className="flex size-full w-full flex-col gap-5">
          {waitingForFetch && (
            <div
              aria-live="assertive"
              className="absolute left-0 top-0 z-20 flex h-full w-full items-center justify-center bg-gray-700 bg-opacity-40"
            >
              <EgSpinner />
            </div>
          )}
          <div className="grid grid-cols-3 items-center">
            <h2 className="col-start-2 text-center text-xl font-medium tracking-tight sm:text-3xl sm:font-normal">
              Time History Report
            </h2>
            <div className="flex w-full justify-end">
              <Button
                onClick={downloadCsv}
                className="gap-2 text-primary-foreground"
                disabled={!reportData?.timeSheet}
              >
                <ArrowDownTrayIcon className="h-4 w-4" />
                Download CSV
              </Button>
            </div>
          </div>
          <div
            className={cn(
              'flex flex-col gap-6',
              import.meta.env.VITE_BETA_BAR_SHOW === 'true'
                ? 'h-[calc(100vh-16rem)]'
                : 'h-[calc(100vh-13rem)]'
            )}
          >
            <div className="max-h-[40%] overflow-hidden">
              <TimeHistoryChart filterData={formData} />
            </div>
            <div className="h-full overflow-hidden">
              <TimeHistoryReportTable filterData={formData} />
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};
